/**
 * Supabase service layer for Teaching Facilitation sessions + photos.
 *
 * Sessions are anonymous, addressed by a short human-readable code
 * (see sessionCode.ts). No auth, no PII — beta posture. Tighten with RLS
 * + user accounts when we introduce paid tier.
 */

import { supabase } from './supabase';
import { generateCode, normalizeCode } from './sessionCode';

const BUCKET = 'teaching-photos';
const MAX_CODE_GENERATION_ATTEMPTS = 8;

export type SkillTag = 'describe' | 'reflect' | 'praise' | 'relate' | 'care';

export interface StudentAvatarRecord {
  index: number;
  emoji: string;
  name?: string;
}

export interface ReflectionNotes {
  surprises?: string;
  growth?: string;
  adjustments?: string;
}

export interface TeachingSessionRow {
  code: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  nickname: string | null;
  language: string | null;
  focus_area: string | null;
  game_title: string | null;
  avatars: StudentAvatarRecord[];
  reflection_notes: ReflectionNotes;
  current_phase: string;
  current_lesson_phase: number;
}

export interface TeachingPhotoRow {
  id: string;
  session_code: string;
  storage_path: string;
  caption: string;
  student_tags: number[];
  skill_tags: SkillTag[];
  sort_index: number;
  created_at: string;
  updated_at: string;
}

export interface PhotoWithUrl extends TeachingPhotoRow {
  publicUrl: string;
}

export type SessionPatch = Partial<
  Pick<
    TeachingSessionRow,
    | 'nickname'
    | 'language'
    | 'focus_area'
    | 'game_title'
    | 'avatars'
    | 'reflection_notes'
    | 'current_phase'
    | 'current_lesson_phase'
  >
>;

export type PhotoPatch = Partial<
  Pick<TeachingPhotoRow, 'caption' | 'student_tags' | 'skill_tags' | 'sort_index'>
>;

function publicUrlFor(storagePath: string): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return data.publicUrl;
}

function attachPublicUrl(row: TeachingPhotoRow): PhotoWithUrl {
  return { ...row, publicUrl: publicUrlFor(row.storage_path) };
}

/**
 * Create a new session row. Retries on code collision.
 */
export async function createSession(initial: SessionPatch = {}): Promise<TeachingSessionRow> {
  for (let attempt = 0; attempt < MAX_CODE_GENERATION_ATTEMPTS; attempt++) {
    const code = generateCode();
    const { data, error } = await supabase
      .from('teaching_sessions')
      .insert({ code, ...initial })
      .select('*')
      .single();

    if (!error && data) return data as TeachingSessionRow;

    // Postgres unique_violation — try again with a fresh code.
    if (error && (error as { code?: string }).code === '23505') continue;
    if (error) throw new Error(`Could not create session: ${error.message}`);
  }
  throw new Error('Could not generate a unique session code after several attempts');
}

/**
 * Load a session by its code. Returns null if not found or expired.
 */
export async function loadSession(
  rawCode: string,
): Promise<{ session: TeachingSessionRow; photos: PhotoWithUrl[] } | null> {
  const code = normalizeCode(rawCode);
  const { data: session, error: sessionErr } = await supabase
    .from('teaching_sessions')
    .select('*')
    .eq('code', code)
    .maybeSingle();

  if (sessionErr) throw new Error(`Could not load session: ${sessionErr.message}`);
  if (!session) return null;

  const typedSession = session as TeachingSessionRow;
  if (new Date(typedSession.expires_at).getTime() < Date.now()) return null;

  const { data: photos, error: photosErr } = await supabase
    .from('teaching_photos')
    .select('*')
    .eq('session_code', code)
    .order('sort_index', { ascending: true })
    .order('created_at', { ascending: true });

  if (photosErr) throw new Error(`Could not load photos: ${photosErr.message}`);

  return {
    session: typedSession,
    photos: (photos ?? []).map((p) => attachPublicUrl(p as TeachingPhotoRow)),
  };
}

/**
 * Apply a partial update to a session. Idempotent.
 */
export async function updateSession(
  code: string,
  patch: SessionPatch,
): Promise<TeachingSessionRow> {
  const { data, error } = await supabase
    .from('teaching_sessions')
    .update(patch)
    .eq('code', normalizeCode(code))
    .select('*')
    .single();

  if (error) throw new Error(`Could not update session: ${error.message}`);
  return data as TeachingSessionRow;
}

/**
 * Upload a photo blob and insert its metadata row. Returns the row with a
 * signed-looking public URL for immediate rendering.
 */
export async function addPhoto(
  code: string,
  blob: Blob,
  meta: PhotoPatch = {},
): Promise<PhotoWithUrl> {
  const normalizedCode = normalizeCode(code);
  const photoId = crypto.randomUUID();
  const storagePath = `${normalizedCode}/${photoId}.jpg`;

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, blob, {
      contentType: 'image/jpeg',
      cacheControl: '3600',
      upsert: false,
    });
  if (uploadErr) throw new Error(`Could not upload photo: ${uploadErr.message}`);

  // Default sort_index = (max existing + 1). Not strictly necessary — created_at
  // breaks ties — but explicit sorting lets future drag-reorder just change this.
  const { data: existing } = await supabase
    .from('teaching_photos')
    .select('sort_index')
    .eq('session_code', normalizedCode)
    .order('sort_index', { ascending: false })
    .limit(1);
  const nextSort = existing && existing.length > 0 ? (existing[0].sort_index as number) + 1 : 0;

  const row = {
    id: photoId,
    session_code: normalizedCode,
    storage_path: storagePath,
    caption: meta.caption ?? '',
    student_tags: meta.student_tags ?? [],
    skill_tags: meta.skill_tags ?? [],
    sort_index: meta.sort_index ?? nextSort,
  };

  const { data, error: insertErr } = await supabase
    .from('teaching_photos')
    .insert(row)
    .select('*')
    .single();

  if (insertErr) {
    // Best-effort cleanup; ignore failure (orphan objects are acceptable in beta).
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => undefined);
    throw new Error(`Could not insert photo row: ${insertErr.message}`);
  }

  return attachPublicUrl(data as TeachingPhotoRow);
}

/**
 * Patch a photo row (caption, tags, sort_index).
 */
export async function updatePhoto(
  photoId: string,
  patch: PhotoPatch,
): Promise<PhotoWithUrl> {
  const { data, error } = await supabase
    .from('teaching_photos')
    .update(patch)
    .eq('id', photoId)
    .select('*')
    .single();

  if (error) throw new Error(`Could not update photo: ${error.message}`);
  return attachPublicUrl(data as TeachingPhotoRow);
}

/**
 * Delete a photo: storage object first, then row. If storage delete fails we
 * still try to remove the row — an orphan storage object is cheaper than a
 * ghost row that keeps reappearing in the UI.
 */
export async function deletePhoto(photo: TeachingPhotoRow): Promise<void> {
  await supabase.storage.from(BUCKET).remove([photo.storage_path]).catch(() => undefined);
  const { error } = await supabase.from('teaching_photos').delete().eq('id', photo.id);
  if (error) throw new Error(`Could not delete photo: ${error.message}`);
}

export function photoPublicUrl(storagePath: string): string {
  return publicUrlFor(storagePath);
}
